import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { UPDATE_TASK, REJECT_TASK, DELETE_TASK } from '../graphql/tasks';

const GET_DOCUMENT_UPLOAD_URL = gql`
  mutation GetDocumentUploadUrl($input: DocumentUploadInput!) {
    getDocumentUploadUrl(input: $input) {
      uploadUrl
      s3Key
    }
  }
`;

interface GetDocumentUploadUrlData {
  getDocumentUploadUrl: {
    uploadUrl: string;
    s3Key: string;
  };
}

const CREATE_DOCUMENT = gql`
  mutation CreateDocument($input: CreateDocumentInput!) {
    createDocument(input: $input) {
      id
      fileName
      fileType
      fileSize
      createdAt
    }
  }
`;

interface TaskDetailModalProps {
  task: any;
  isOpen: boolean;
  onClose: () => void;
  onTaskUpdated?: () => void;
}

export function TaskDetailModal({ task, isOpen, onClose, onTaskUpdated }: TaskDetailModalProps) {
  const navigate = useNavigate();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionNotes, setRejectionNotes] = useState('');
  
  // Local state for task status to update UI immediately
  const [currentStatus, setCurrentStatus] = useState(task?.status || 'PENDING');
  
  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileDescription, setFileDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadSection, setShowUploadSection] = useState(false);

  const [updateTask] = useMutation(UPDATE_TASK);
  const [rejectTask] = useMutation(REJECT_TASK);
  const [deleteTask] = useMutation(DELETE_TASK);
  const [getDocumentUploadUrl] = useMutation<GetDocumentUploadUrlData>(GET_DOCUMENT_UPLOAD_URL);
  const [createDocument] = useMutation(CREATE_DOCUMENT);

  // Sync local status state when task prop changes
  useEffect(() => {
    if (task?.status) {
      setCurrentStatus(task.status);
    }
  }, [task?.status]);

  if (!isOpen || !task) return null;

  const handleStatusChange = async (newStatus: string) => {
    try {
      // Update local state immediately for responsive UI
      setCurrentStatus(newStatus);
      
      await updateTask({
        variables: {
          id: task.id,
          input: { status: newStatus }
        },
        refetchQueries: ['ListTasksByCase'],
        awaitRefetchQueries: true,
      });
      
      if (onTaskUpdated) {
        onTaskUpdated();
      }
      
      alert(`Task status updated to ${newStatus}`);
    } catch (error: any) {
      // Revert local state on error
      setCurrentStatus(task.status);
      console.error('Error updating task status:', error);
      alert('Error updating task status: ' + error.message);
    }
  };

  const handleRejectTask = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      await rejectTask({
        variables: {
          id: task.id,
          rejectionReason,
          staffNotes: rejectionNotes || undefined
        },
        refetchQueries: ['ListTasksByCase'],
        awaitRefetchQueries: true,
      });
      
      if (onTaskUpdated) {
        onTaskUpdated();
      }
      
      setShowRejectModal(false);
      setRejectionReason('');
      setRejectionNotes('');
      alert('Task rejected. Client has been notified.');
      onClose();
    } catch (error: any) {
      console.error('Error rejecting task:', error);
      alert('Error rejecting task: ' + error.message);
    }
  };

  const handleDeleteTask = async () => {
    if (!confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      await deleteTask({
        variables: { id: task.id },
        refetchQueries: ['ListTasksByCase'],
        awaitRefetchQueries: true,
      });
      
      if (onTaskUpdated) {
        onTaskUpdated();
      }
      
      alert('Task deleted successfully');
      onClose();
    } catch (error: any) {
      console.error('Error deleting task:', error);
      alert('Error deleting task: ' + error.message);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        alert('File size must be less than 50MB');
        return;
      }

      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];

      if (!allowedTypes.includes(file.type)) {
        alert('File type not supported. Please upload PDF, JPEG, PNG, or DOCX files.');
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    if (!task.caseId) {
      alert('Cannot upload file: Task is not associated with a case');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Get pre-signed upload URL
      const { data: uploadData } = await getDocumentUploadUrl({
        variables: {
          input: {
            caseId: task.caseId,
            fileName: selectedFile.name,
            fileType: selectedFile.type,
            fileSize: selectedFile.size,
          },
        },
      });

      if (!uploadData?.getDocumentUploadUrl) {
        throw new Error('Failed to get upload URL');
      }

      const uploadUrl = uploadData.getDocumentUploadUrl.uploadUrl;
      const s3Key = uploadData.getDocumentUploadUrl.s3Key;

      setUploadProgress(30);

      // Step 2: Upload file to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      setUploadProgress(70);

      // Step 3: Create document record
      const description = fileDescription || `Attachment for task: ${task.title}`;
      await createDocument({
        variables: {
          input: {
            caseId: task.caseId,
            fileName: selectedFile.name,
            fileType: selectedFile.type,
            fileSize: selectedFile.size,
            s3Key,
            description,
          },
        },
        refetchQueries: ['ListDocumentsByCase'],
        awaitRefetchQueries: true,
      });

      setUploadProgress(100);

      // Reset form
      setSelectedFile(null);
      setFileDescription('');
      setUploadProgress(0);
      setShowUploadSection(false);

      alert('File uploaded successfully!');
      
      if (onTaskUpdated) {
        onTaskUpdated();
      }
    } catch (error: any) {
      console.error('Error uploading file:', error);
      alert('Error uploading file: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const getTaskPriorityBadgeVariant = (priority: string): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    const variants: Record<string, any> = {
      LOW: 'secondary',
      MEDIUM: 'info',
      HIGH: 'warning',
      URGENT: 'default',
    };
    return variants[priority] || 'default';
  };

  const getTaskStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    const variants: Record<string, any> = {
      PENDING: 'warning',
      IN_PROGRESS: 'info',
      COMPLETED: 'success',
      VERIFIED: 'success',
      REJECTED: 'default',
      OVERDUE: 'default',
    };
    return variants[status] || 'default';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <>
      {/* Task Detail Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
              {task.caseNumber && task.clientName && (
                <p className="text-sm text-gray-600 mt-1">
                  {task.caseNumber} • {task.clientName}
                </p>
              )}
              <div className="flex gap-2 mt-2">
                <Badge variant={getTaskPriorityBadgeVariant(task.priority)}>
                  {task.priority}
                </Badge>
                <Badge variant={getTaskStatusBadgeVariant(currentStatus)}>
                  {currentStatus}
                </Badge>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4 mb-6 max-h-[60vh] overflow-y-auto">
            <div>
              <label className="text-sm font-medium text-gray-600">Description</label>
              <p className="text-sm text-gray-900 mt-1">{task.description}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Task Type</label>
                <p className="text-sm text-gray-900 mt-1">{task.taskType}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Due Date</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(task.dueDate)}</p>
              </div>
            </div>
            
            {/* Status Change Dropdown for Staff */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Change Task Status
              </label>
              <select
                value={currentStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="PENDING">Pending</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="VERIFIED">Verified</option>
                <option value="REJECTED">Rejected</option>
              </select>
              <p className="text-xs text-gray-600 mt-1">
                Staff can manually change the task status at any time
              </p>
            </div>

            {task.staffNotes && (
              <div>
                <label className="text-sm font-medium text-gray-600">Staff Notes</label>
                <p className="text-sm text-gray-900 mt-1">{task.staffNotes}</p>
              </div>
            )}
            {task.clientNotes && (
              <div>
                <label className="text-sm font-medium text-gray-600">Client Notes</label>
                <p className="text-sm text-gray-900 mt-1">{task.clientNotes}</p>
              </div>
            )}

            {/* File Upload Section */}
            <div className="border-t border-gray-200 pt-4">
              {!showUploadSection ? (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowUploadSection(true)}
                  disabled={!task.caseId}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  Attach File to Task
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">Upload File</h4>
                    <button
                      onClick={() => {
                        setShowUploadSection(false);
                        setSelectedFile(null);
                        setFileDescription('');
                      }}
                      className="text-gray-400 hover:text-gray-600"
                      disabled={uploading}
                    >
                      ✕
                    </button>
                  </div>

                  {/* File Input */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Select File
                    </label>
                    <input
                      type="file"
                      onChange={handleFileSelect}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                      disabled={uploading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      PDF, JPEG, PNG, DOCX (Max 50MB)
                    </p>
                  </div>

                  {/* Selected File Info */}
                  {selectedFile && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                      <p className="text-xs font-medium text-blue-900">Selected:</p>
                      <p className="text-xs text-blue-700">{selectedFile.name}</p>
                      <p className="text-xs text-blue-600">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  )}

                  {/* Description Input */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      value={fileDescription}
                      onChange={(e) => setFileDescription(e.target.value)}
                      placeholder="Add a description..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={uploading}
                    />
                  </div>

                  {/* Upload Progress */}
                  {uploading && (
                    <div className="space-y-1">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-600 text-center">
                        Uploading... {uploadProgress}%
                      </p>
                    </div>
                  )}

                  {/* Upload Button */}
                  <Button
                    onClick={handleFileUpload}
                    disabled={!selectedFile || uploading}
                    style={{ backgroundColor: '#3B82F6', color: 'white' }}
                    className="w-full hover:bg-blue-700"
                  >
                    {uploading ? 'Uploading...' : 'Upload File'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onClose}
            >
              Close
            </Button>
            {task.caseId && (
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate(`/cases/${task.caseId}`)}
              >
                View Case
              </Button>
            )}
            {(task.status === 'PENDING' || task.status === 'REJECTED') && (
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDeleteTask}
              >
                Delete Task
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Reject Task Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Task</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why this task is being rejected..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Staff Notes (Optional)
                </label>
                <textarea
                  value={rejectionNotes}
                  onChange={(e) => setRejectionNotes(e.target.value)}
                  placeholder="Internal notes..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  setRejectionNotes('');
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleRejectTask}
              >
                Reject Task
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
